import _ from 'underscore'
import React from 'react'
import PropTypes from 'prop-types'

import Form from '../elements/Form'
import TextInput from '../elements/forms/TextInput'
import PasswordInput from '../elements/forms/PasswordInput'
import CheckboxInput from '../elements/forms/CheckboxInput'
import Dismissible from '../elements/Dismissible'
import AppConstants from '../../utils/AppConstants'
import AppUtils from '../../utils/AppUtils'

const ERROR = {
  EMPTY_NAME: "Les nom et prénom ne peuvent pas être vides.",
  EMPTY_USERNAME: "Le nom d'utilisateur ne peut pas être vide.",
  EMPTY_PASSWORD: "Le mot de passe doit faire au moins 8 caractères."
}

class UserForm extends React.Component {

  constructor(props) {

    // Properties
    super(props)

    // State
    this.state = {
      input: this.getInitialInput(props),
      formErrors: [],
      isValid: true
    }

    // Bind events
    this.onChangeInputName = this.onChangeInputName.bind(this)
    this.onChangeInputUsername = this.onChangeInputUsername.bind(this)
    this.onChangeCheckboxPassword = this.onChangeCheckboxPassword.bind(this)
    this.onChangeInputPassword = this.onChangeInputPassword.bind(this)
    this.onChangeCheckboxLogin = this.onChangeCheckboxLogin.bind(this)
    this.onChangeCheckboxInventory = this.onChangeCheckboxInventory.bind(this)
    this.onChangeCheckboxMaintenance = this.onChangeCheckboxMaintenance.bind(this)
    this.onChangeCheckboxManage = this.onChangeCheckboxManage.bind(this)
    this.onChangeCheckboxAdmin = this.onChangeCheckboxAdmin.bind(this)
    this.onSubmit = this.onSubmit.bind(this)

    // Register submit event
    if (_.isFunction(this.props.registerSubmit)) {
      this.props.registerSubmit(this.onSubmit)
    }
  }

  getInitialInput(props = this.props) {
    return {
      name: (props.data && props.data.name) || '',
      username: (props.data && props.data.username) || '',
      password: props.data ? null : '',
      authorizations: (props.data && props.data.authorizations) || {
        login: true,
        inventory: false,
        maintenance: false,
        manage: false,
        admin: false
      }
    }
  }

  setInput(input = {}) {
    this.setState((prevState, props) => ({
      input: _.extend(
        {},
        prevState.input,
        input
      )
    }))
  }

  setAuthorization(authorizations = {}) {
    if (authorizations.inventory) {
      _.extend(authorizations, { login: true })
    }
    if (authorizations.maintenance) {
      _.extend(authorizations, { login: true })
    }
    if (authorizations.manage) {
      _.extend(authorizations, {
        login: true,
        inventory: true,
        maintenance: true
      })
    }
    if (authorizations.admin) {
      _.extend(authorizations, {
        login: true,
        inventory: true,
        maintenance: true,
        manage: true
      })
    }
    this.setState((prevState, props) => ({
      input: _.extend(
        {},
        prevState.input,
        {
          authorizations: _.extend(
            {},
            prevState.input.authorizations,
            authorizations
          )
        }
      )
    }))
  }

  componentDidUpdate(prevProps, prevState) {
    // When async fetch finished
    if (prevProps.isLoading && !this.props.isLoading) {
      this.setState({
        input: this.getInitialInput()
      })
    }
  }

  onChangeInputName(name) {
    this.setInput({ name })
  }

  onChangeInputUsername(username) {
    this.setInput({ username })
  }

  onChangeCheckboxPassword(checked) {
    this.setState((prevState, props) => ({
      isValid: true,
      input: _.extend(
        {},
        prevState.input,
        {
          password: checked ? '' : null
        }
      )
    }))
  }

  onChangeInputPassword(password, isValid) {
    this.setState((prevState, props) => (_.extend(
      {
        isValid
      },
      isValid
        ? {
            input: _.extend(
              {},
              prevState.input,
              {
                password
              }
            )
          }
        : {}
    )))
  }

  onChangeCheckboxLogin(login) {
    this.setAuthorization({ login })
  }

  onChangeCheckboxInventory(inventory) {
    this.setAuthorization({ inventory })
  }

  onChangeCheckboxMaintenance(maintenance) {
    this.setAuthorization({ maintenance })
  }

  onChangeCheckboxManage(manage) {
    this.setAuthorization({ manage })
  }

  onChangeCheckboxAdmin(admin) {
    this.setAuthorization({ admin })
  }

  onSubmit() {
    // Check that fields have been filled
    const formErrors = []
    if (!_.isString(this.state.input.name) || this.state.input.name.length === 0) {
      formErrors.push(ERROR.EMPTY_NAME)
    }
    if (!_.isString(this.state.input.username) || this.state.input.username.length === 0) {
      formErrors.push(ERROR.EMPTY_USERNAME)
    }
    if (_.isString(this.state.input.password) && this.state.input.password.length < 8) {
      formErrors.push(ERROR.EMPTY_PASSWORD)
    }
    this.setState({
      formErrors
    })
    if (!_.isEmpty(formErrors) || !this.state.isValid) {
      return;
    }

    // Build and submit correctly formatted input
    this.props.onSubmit(_.omit(this.state.input, _.isNull))
  }

  render() {
    return (
      <React.Fragment>
        {
          this.props.error
            ? <Dismissible
                header={AppUtils.errorToTitle(this.props.error)}
                content={AppUtils.errorToText(this.props.error, {
                  [AppConstants.ERROR.BAD_REQUEST.UNICITY_CONSTRAINT_VIOLATION]: "Ce nom d'utilisateur existe déjà."
                })}
                color='red'
                onDismiss={this.props.onClearError}
              />
            : null
        }
        {
          !_.isEmpty(this.state.formErrors)
            ? <Dismissible
                content='Veuillez résoudre les problèmes ci-dessous avant de poursuivre.'
                color='orange'
              />
            : null
        }
        <Form
          onSubmit={this.onSubmit}
          loading={this.props.isLoading || this.props.isSaving}
        >
          <TextInput
            label='Nom et prénom'
            placeholder='Jean Dupond'
            value={this.state.input.name}
            onChange={this.onChangeInputName}
            error={
              _.contains(this.state.formErrors, ERROR.EMPTY_NAME)
                ? ERROR.EMPTY_NAME
                : null
            }
          />
          <TextInput
            label="Nom d'utilisateur"
            placeholder='jean.dupond'
            value={this.state.input.username}
            onChange={this.onChangeInputUsername}
            error={
              _.contains(this.state.formErrors, ERROR.EMPTY_USERNAME)
                ? ERROR.EMPTY_USERNAME
                : null
            }
          />
          {
            this.props.data
              ? <CheckboxInput
                  label=''
                  text='Changer le mot de passe'
                  checked={!_.isNull(this.state.input.password)}
                  onChange={this.onChangeCheckboxPassword}
                />
              : null
          }
          {
            !_.isNull(this.state.input.password)
              ? <PasswordInput
                  label1='Mot de passe'
                  label2='Répéter'
                  value={this.state.input.password}
                  onChange={this.onChangeInputPassword}
                  error={
                    _.contains(this.state.formErrors, ERROR.EMPTY_PASSWORD)
                      ? ERROR.EMPTY_PASSWORD
                      : null
                  }
                />
              : null
          }
          <CheckboxInput
            label='Autorisations'
            text='Connexion'
            checked={this.state.input.authorizations.login}
            onChange={this.onChangeCheckboxLogin}
            disabled={
              this.state.input.authorizations.inventory
              ||
              this.state.input.authorizations.maintenance
              ||
              this.state.input.authorizations.manage
              ||
              this.state.input.authorizations.admin
            }
          />
          <CheckboxInput
            label=''
            text='Inventaire'
            checked={this.state.input.authorizations.inventory}
            onChange={this.onChangeCheckboxInventory}
            smallPadding={true}
            disabled={
              this.state.input.authorizations.manage
              ||
              this.state.input.authorizations.admin
            }
          />
          <CheckboxInput
            label=''
            text='Maintenance'
            checked={this.state.input.authorizations.maintenance}
            onChange={this.onChangeCheckboxMaintenance}
            smallPadding={true}
            disabled={
              this.state.input.authorizations.manage
              ||
              this.state.input.authorizations.admin
            }
          />
          <CheckboxInput
            label=''
            text='Gestion'
            checked={this.state.input.authorizations.manage}
            onChange={this.onChangeCheckboxManage}
            smallPadding={true}
            disabled={
              this.state.input.authorizations.admin
            }
          />
          <CheckboxInput
            label=''
            text='Administrateur'
            checked={this.state.input.authorizations.admin}
            onChange={this.onChangeCheckboxAdmin}
            smallPadding={true}
          />
        </Form>
      </React.Fragment>
    )
  }
}

UserForm.propTypes = {
  data: PropTypes.object,
  onSubmit: PropTypes.func.isRequired,
  onClearError: PropTypes.func.isRequired,
  isLoading: PropTypes.bool.isRequired,
  isSaving: PropTypes.bool.isRequired,
  registerSubmit: PropTypes.func,
  error: PropTypes.object
}

export default UserForm



// WEBPACK FOOTER //
// ./src/components/forms/UserForm.js